// ============================================================================
// CHIP DUMPING DETECTION SERVICE - Chip Flow Analysis
// ============================================================================
// This service tracks chip flows between players to detect intentional
// chip dumping patterns (one player consistently losing to another).
// Creates FLAGS for human review - NO auto-bans.
// ============================================================================

import {
  collection,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  query,
  where,
  getDocs,
  Timestamp,
  increment,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  ChipFlowRecord,
  SuspiciousHand,
  ChipTransfer,
  TransferParticipant,
  FLAG_THRESHOLDS,
} from '../../types/securityTypes';
import securityFlagService from './securityFlagService';

// ============================================================================
// CONSTANTS
// ============================================================================

const CHIP_FLOWS_COLLECTION = 'chipFlows';

// Analysis thresholds
const MIN_HANDS_FOR_ANALYSIS = 15;         // Need at least 15 hands
const ANALYSIS_FREQUENCY_HANDS = 20;        // Analyze every 20 hands
const MAX_SUSPICIOUS_HANDS_STORED = 100;    // Store up to 100 suspicious hands

// Dumping detection thresholds
const UNIDIRECTIONAL_FLOW_RATIO = 0.8;     // 80%+ flow one direction = suspicious
const LARGE_TRANSFER_MULTIPLIER = 10;       // 10x average = large transfer
const MIN_SIGNIFICANT_FLOW = 500;           // Minimum chip flow to flag

// ============================================================================
// CHIP DUMPING SERVICE CLASS
// ============================================================================

class ChipDumpingService {
  // ============================================================================
  // CHIP TRANSFER RECORDING
  // ============================================================================

  /**
   * Record chip transfers from a hand result
   * Called after showdown
   */
  async recordChipTransfer(transfer: ChipTransfer): Promise<void> {
    try {
      const { tableId, handNumber, fromPlayers, toPlayers, timestamp } = transfer;

      console.log(`💰 [ChipDump] Recording transfer: ${fromPlayers.length} losers -> ${toPlayers.length} winners`);

      // For each loser-winner pair, record the flow
      for (const loser of fromPlayers) {
        for (const winner of toPlayers) {
          if (loser.playerId === winner.playerId) continue;

          // Calculate proportional amount this loser contributed to this winner
          const totalWinnings = toPlayers.reduce((sum, p) => sum + p.amount, 0);
          const winnerShare = winner.amount / totalWinnings;
          const amountToWinner = loser.amount * winnerShare;

          if (amountToWinner > 0) {
            await this.updateChipFlow(
              loser.playerId,
              winner.playerId,
              amountToWinner,
              tableId,
              handNumber,
              timestamp
            );
          }
        }
      }

    } catch (error) {
      console.error('❌ [ChipDump] Error recording chip transfer:', error);
    }
  }

  /**
   * Update chip flow record between two players
   */
  private async updateChipFlow(
    fromPlayerId: string,
    toPlayerId: string,
    amount: number,
    tableId: string,
    handNumber: number,
    timestamp: Timestamp
  ): Promise<void> {
    const flowId = this.getFlowId(fromPlayerId, toPlayerId);
    const flowRef = doc(db, CHIP_FLOWS_COLLECTION, flowId);
    const [p1, p2] = this.sortPlayerIds(fromPlayerId, toPlayerId);

    try {
      const flowDoc = await getDoc(flowRef);

      if (flowDoc.exists()) {
        const record = flowDoc.data() as ChipFlowRecord;

        // Determine which direction flow is going
        const flowToP2 = fromPlayerId === p1;
        
        const updates: any = {
          handsPlayed: increment(1),
          lastUpdated: Timestamp.now(),
        };

        if (flowToP2) {
          updates.totalFlowToPlayer2 = increment(amount);
          updates.netFlow = increment(amount);
        } else {
          updates.totalFlowToPlayer1 = increment(amount);
          updates.netFlow = increment(-amount);
        }

        // Check if this is a large transfer
        const avgTransfer = record.handsPlayed > 0 
          ? (record.totalFlowToPlayer1 + record.totalFlowToPlayer2) / record.handsPlayed 
          : amount;

        if (amount > avgTransfer * LARGE_TRANSFER_MULTIPLIER) {
          updates.largestSingleTransfer = Math.max(record.largestSingleTransfer, amount);
          
          // Add to suspicious hands
          const suspiciousHand: SuspiciousHand = {
            tableId,
            handNumber,
            amount,
            reason: `Large transfer: ${amount} (avg: ${avgTransfer.toFixed(0)})`,
            timestamp,
          };

          const suspiciousHands = [...record.suspiciousHands, suspiciousHand]
            .slice(-MAX_SUSPICIOUS_HANDS_STORED);

          updates.suspiciousHands = suspiciousHands;
        }

        await updateDoc(flowRef, updates);

        // Check if we should run analysis
        const newHandCount = record.handsPlayed + 1;
        if (newHandCount % ANALYSIS_FREQUENCY_HANDS === 0 &&
            newHandCount >= MIN_HANDS_FOR_ANALYSIS) {
          await this.analyzeChipFlow(fromPlayerId, toPlayerId);
        }

      } else {
        // Create new flow record
        const flowToP2 = fromPlayerId === p1;

        const newRecord: ChipFlowRecord = {
          player1Id: p1,
          player2Id: p2,
          totalFlowToPlayer1: flowToP2 ? 0 : amount,
          totalFlowToPlayer2: flowToP2 ? amount : 0,
          netFlow: flowToP2 ? amount : -amount,
          handsPlayed: 1,
          largestSingleTransfer: amount,
          avgTransferSize: amount,
          suspiciousHands: [],
          dumpScore: 0,
          lastUpdated: Timestamp.now(),
        };

        await setDoc(flowRef, newRecord);
      }

    } catch (error) {
      console.error('❌ [ChipDump] Error updating chip flow:', error);
    }
  }

  /**
   * Generate consistent flow ID for two players
   */
  private getFlowId(player1Id: string, player2Id: string): string {
    const [p1, p2] = this.sortPlayerIds(player1Id, player2Id);
    return `${p1}_${p2}`;
  }

  /**
   * Sort player IDs alphabetically for consistency
   */
  private sortPlayerIds(player1Id: string, player2Id: string): [string, string] {
    return player1Id < player2Id 
      ? [player1Id, player2Id] 
      : [player2Id, player1Id];
  }

  // ============================================================================
  // CHIP DUMPING ANALYSIS
  // ============================================================================

  /**
   * Analyze chip flow between two players for dumping patterns
   */
  async analyzeChipFlow(player1Id: string, player2Id: string): Promise<number> {
    try {
      console.log(`🔍 [ChipDump] Analyzing flow: ${player1Id.substring(0, 8)} <-> ${player2Id.substring(0, 8)}`);

      const flowId = this.getFlowId(player1Id, player2Id);
      const flowRef = doc(db, CHIP_FLOWS_COLLECTION, flowId);
      const flowDoc = await getDoc(flowRef);

      if (!flowDoc.exists()) {
        return 0;
      }

      const record = flowDoc.data() as ChipFlowRecord;

      if (record.handsPlayed < MIN_HANDS_FOR_ANALYSIS) {
        console.log(`ℹ️ [ChipDump] Not enough hands (${record.handsPlayed})`);
        return 0;
      }

      let dumpScore = 0;
      const signals: string[] = [];
      const dataPoints: any[] = [];

      // Calculate total flow and direction
      const totalFlow = record.totalFlowToPlayer1 + record.totalFlowToPlayer2;
      const netFlowAbs = Math.abs(record.netFlow);
      
      if (totalFlow < MIN_SIGNIFICANT_FLOW) {
        // Not enough chips transferred to analyze
        return 0;
      }

      // Check 1: Unidirectional flow
      const flowRatio = netFlowAbs / totalFlow;
      if (flowRatio > UNIDIRECTIONAL_FLOW_RATIO) {
        const points = Math.min(40, Math.round(flowRatio * 50));
        dumpScore += points;
        signals.push('UNIDIRECTIONAL_FLOW');
        
        const direction = record.netFlow > 0 ? 'P1 -> P2' : 'P2 -> P1';
        dataPoints.push({
          type: 'flow_direction',
          description: `${Math.round(flowRatio * 100)}% of chips flow ${direction}`,
          value: flowRatio,
          weight: points,
        });
      }

      // Check 2: Large net flow
      if (netFlowAbs > 5000) {
        const points = Math.min(20, Math.round(netFlowAbs / 500));
        dumpScore += points;
        signals.push('LARGE_NET_TRANSFER');
        dataPoints.push({
          type: 'net_flow',
          description: `Net chip flow: ${netFlowAbs.toFixed(0)}`,
          value: netFlowAbs,
          weight: points,
        });
      }

      // Check 3: Suspicious hand frequency
      const suspiciousRate = record.suspiciousHands.length / record.handsPlayed;
      if (suspiciousRate > 0.1) {
        const points = Math.min(20, Math.round(suspiciousRate * 100));
        dumpScore += points;
        signals.push('HIGH_SUSPICIOUS_HAND_RATE');
        dataPoints.push({
          type: 'suspicious_rate',
          description: `${Math.round(suspiciousRate * 100)}% of hands flagged as suspicious`,
          value: suspiciousRate,
          weight: points,
        });
      }

      // Check 4: Large single transfers
      const avgTransfer = totalFlow / record.handsPlayed;
      if (record.largestSingleTransfer > avgTransfer * LARGE_TRANSFER_MULTIPLIER) {
        dumpScore += 15;
        signals.push('LARGE_SINGLE_TRANSFER');
        dataPoints.push({
          type: 'max_transfer',
          description: `Largest transfer: ${record.largestSingleTransfer.toFixed(0)} (${(record.largestSingleTransfer / avgTransfer).toFixed(1)}x average)`,
          value: record.largestSingleTransfer,
          weight: 15,
        });
      }

      // Normalize score to 0-100
      dumpScore = Math.min(100, dumpScore);

      console.log(`💰 [ChipDump] Score: ${dumpScore}% (${signals.length} signals)`);

      // Update record with score
      await updateDoc(flowRef, {
        dumpScore,
        avgTransferSize: avgTransfer,
        lastUpdated: Timestamp.now(),
      });

      // Create flag if threshold met
      if (dumpScore >= FLAG_THRESHOLDS.CHIP_DUMP_MEDIUM) {
        // Determine who is the dumper (chips flow FROM them)
        const dumperId = record.netFlow > 0 ? record.player1Id : record.player2Id;
        const receiverId = record.netFlow > 0 ? record.player2Id : record.player1Id;

        const hasExisting = await securityFlagService.hasExistingFlag(
          'chip_dumping',
          dumperId,
          receiverId,
          168 // 1 week
        );

        if (!hasExisting) {
          const severity = dumpScore >= FLAG_THRESHOLDS.CHIP_DUMP_HIGH ? 'high' : 'medium';

          await securityFlagService.createFlag({
            type: 'chip_dumping',
            severity,
            confidence: dumpScore,
            primaryUserId: dumperId,
            relatedUserId: receiverId,
            evidence: {
              signals,
              dataPoints,
              summary: `Chip dumping score ${dumpScore}%: ${netFlowAbs.toFixed(0)} chips flowed to ${receiverId.substring(0, 8)}`,
            },
          });
        }
      }

      return dumpScore;

    } catch (error) {
      console.error('❌ [ChipDump] Error analyzing chip flow:', error);
      return 0;
    }
  }

  /**
   * Get chip flow record between two players
   */
  async getChipFlow(player1Id: string, player2Id: string): Promise<ChipFlowRecord | null> {
    try {
      const flowId = this.getFlowId(player1Id, player2Id);
      const flowRef = doc(db, CHIP_FLOWS_COLLECTION, flowId);
      const flowDoc = await getDoc(flowRef);

      if (flowDoc.exists()) {
        return flowDoc.data() as ChipFlowRecord;
      }

      return null;

    } catch (error) {
      console.error('❌ [ChipDump] Error getting chip flow:', error);
      return null;
    }
  }

  /**
   * Get all suspicious chip flow relationships for a player
   */
  async getSuspiciousFlows(
    playerId: string,
    minScore: number = 50
  ): Promise<ChipFlowRecord[]> {
    try {
      // Query flows where player is involved with high score
      const q1 = query(
        collection(db, CHIP_FLOWS_COLLECTION),
        where('player1Id', '==', playerId),
        where('dumpScore', '>=', minScore)
      );

      const q2 = query(
        collection(db, CHIP_FLOWS_COLLECTION),
        where('player2Id', '==', playerId),
        where('dumpScore', '>=', minScore)
      );

      const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)]);

      const flows: ChipFlowRecord[] = [];
      snap1.docs.forEach(doc => flows.push(doc.data() as ChipFlowRecord));
      snap2.docs.forEach(doc => flows.push(doc.data() as ChipFlowRecord));

      return flows;

    } catch (error) {
      console.error('❌ [ChipDump] Error getting suspicious flows:', error);
      return [];
    }
  }

  /**
   * Get all high-score chip flows (for admin dashboard)
   */
  async getHighScoreFlows(
    minScore: number = 60,
    limitCount: number = 50
  ): Promise<ChipFlowRecord[]> {
    try {
      const q = query(
        collection(db, CHIP_FLOWS_COLLECTION),
        where('dumpScore', '>=', minScore)
      );

      const snapshot = await getDocs(q);
      const flows: ChipFlowRecord[] = [];
      
      snapshot.docs.forEach(doc => {
        flows.push(doc.data() as ChipFlowRecord);
      });

      // Sort by score descending
      flows.sort((a, b) => b.dumpScore - a.dumpScore);

      return flows.slice(0, limitCount);

    } catch (error) {
      console.error('❌ [ChipDump] Error getting high score flows:', error);
      return [];
    }
  }

  /**
   * Get suspicious hands for a player pair
   */
  async getSuspiciousHands(
    player1Id: string,
    player2Id: string
  ): Promise<SuspiciousHand[]> {
    const flow = await this.getChipFlow(player1Id, player2Id);
    return flow?.suspiciousHands || [];
  }

  /**
   * Force re-analysis of a player pair
   */
  async forceReanalyze(player1Id: string, player2Id: string): Promise<number> {
    return this.analyzeChipFlow(player1Id, player2Id);
  }

  /**
   * Calculate chip dump score for a player (based on all their flows)
   */
  async calculatePlayerDumpScore(playerId: string): Promise<number> {
    try {
      const flows = await this.getSuspiciousFlows(playerId, 0);
      
      if (flows.length === 0) return 0;

      // Average of all flow scores
      const totalScore = flows.reduce((sum, f) => sum + f.dumpScore, 0);
      return Math.round(totalScore / flows.length);

    } catch (error) {
      console.error('❌ [ChipDump] Error calculating player dump score:', error);
      return 0;
    }
  }

  /**
   * Get total chips a player has dumped to others
   */
  async getTotalChipsDumped(playerId: string): Promise<number> {
    try {
      // Query all flows where this player is involved
      const q1 = query(
        collection(db, CHIP_FLOWS_COLLECTION),
        where('player1Id', '==', playerId)
      );

      const q2 = query(
        collection(db, CHIP_FLOWS_COLLECTION),
        where('player2Id', '==', playerId)
      );

      const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)]);

      let totalDumped = 0;

      // For flows where player is p1, they dump if netFlow > 0
      snap1.docs.forEach(doc => {
        const flow = doc.data() as ChipFlowRecord;
        if (flow.netFlow > 0) {
          totalDumped += flow.netFlow;
        }
      });

      // For flows where player is p2, they dump if netFlow < 0
      snap2.docs.forEach(doc => {
        const flow = doc.data() as ChipFlowRecord;
        if (flow.netFlow < 0) {
          totalDumped += Math.abs(flow.netFlow);
        }
      });

      return totalDumped;

    } catch (error) {
      console.error('❌ [ChipDump] Error getting total chips dumped:', error);
      return 0;
    }
  }
}

// ============================================================================
// SINGLETON EXPORT
// ============================================================================

const chipDumpingService = new ChipDumpingService();
export { chipDumpingService };
export default chipDumpingService;